import { REDACTED_PATH_PARAM } from "./constants";
import {
  emailRegex,
  emailSegmentRegex,
  numberRegex,
  redactApiKeyRegex,
  redactBillingInvoiceRegex,
  redactDynamicTemplateRegex,
  redactOfferingsRegex,
  redactPaymentRegex,
  redactSubusersRegex,
  redactTeammateRegex,
  redactVerifySenderRegex,
  uuidv4Regex,
} from "./regex";

const decodeSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return undefined;
  }
};

/**
 * Redacts percent-encoded email segments, e.g. bob%40example.com from encodeURIComponent(email).
 * Each segment is decoded on its own, so an encoded "/" cannot change the path structure.
 */
const redactEncodedEmailSegments = (pathname: string) =>
  pathname
    .split("/")
    .map((segment) => {
      if (!segment.includes("%")) {
        return segment;
      }
      const decoded = decodeSegment(segment);
      return decoded && emailSegmentRegex.test(decoded) ? REDACTED_PATH_PARAM : segment;
    })
    .join("/");

/** Redacts numeric, email (literal or percent-encoded), and UUID path segments from a URL. */
export const redactPathParams = (urlString: string) => {
  const url = new URL(urlString);

  url.pathname = redactEncodedEmailSegments(url.pathname)
    .replace(numberRegex, REDACTED_PATH_PARAM)
    .replace(emailRegex, REDACTED_PATH_PARAM)
    .replace(uuidv4Regex, REDACTED_PATH_PARAM);

  return url.toString();
};

/** Redacts identifiers that follow known SendGrid API endpoints (api_keys, teammates, etc). */
export const redactSpecificAPIPathParams = (urlString: string) => {
  const url = new URL(urlString);

  url.pathname = url.pathname
    .replace(redactApiKeyRegex, REDACTED_PATH_PARAM)
    .replace(redactBillingInvoiceRegex, REDACTED_PATH_PARAM)
    .replace(redactVerifySenderRegex, REDACTED_PATH_PARAM)
    .replace(redactTeammateRegex, REDACTED_PATH_PARAM)
    .replace(redactSubusersRegex, REDACTED_PATH_PARAM)
    .replace(redactOfferingsRegex, REDACTED_PATH_PARAM);

  return url.toString();
};

/** Redacts identifiers that follow known app page routes (dynamic templates, payment). */
export const redactSpecificViewPathParams = (urlString: string) => {
  const url = new URL(urlString);

  url.pathname = url.pathname
    .replace(redactDynamicTemplateRegex, REDACTED_PATH_PARAM)
    .replace(redactPaymentRegex, `${REDACTED_PATH_PARAM}/${REDACTED_PATH_PARAM}`);

  return url.toString();
};
